/**
 * ConnectorPicker — multi-select of enterprise/data connectors to scope
 * the next query against. Surfaced when the focus mode is `structured`
 * or `full` (the BE filters search by these connection_ids).
 *
 * v1 ref: `QueryInput.tsx` → `GroupedConnectorPicker` (200-LoC popover).
 * v2 keeps the surface lean but restores the grouping the user liked:
 * connectors are bucketed per **azienda** (P.IVA), each row carries a
 * **category** badge (Database / ERP / Spreadsheet), groups are collapsible
 * with a tri-state "select all", and a footer shows the live count + a
 * one-click "deselect all". No virtual scroll: enterprise connector counts
 * are small (tens, not thousands), so a plain ``max-h`` scroll container is
 * simpler and avoids a dependency.
 */

import {
  Building2,
  Calculator,
  ChevronDown,
  Compass,
  Database,
  Folder,
  Landmark,
  type LucideIcon,
  Receipt,
  Sheet,
  Users,
} from "lucide-react";
import * as React from "react";
import { cn } from "../../lib/cn";
import { Checkbox } from "../../primitives/checkbox";

/**
 * Category → glyph map (June 11, 2026 UX pass).
 *
 * Mirrors the per-type iconography already used elsewhere in the
 * composer (Folder for folders, FileText for files) so the user can
 * scan the connector list by category at a glance — same affordance
 * the badge gives via text, repeated visually on the left edge.
 *
 * Match is case-insensitive against the canonical labels emitted by
 * the caller (see ``CATEGORY_LABEL`` in ``useStructuredDataConnectors``);
 * any unknown ``category`` value falls back to a generic ``Database``
 * glyph so the row never renders icon-less. Keep additions short — the
 * point is one-glance distinction, not a taxonomy.
 */
const CATEGORY_ICON: Readonly<Record<string, LucideIcon>> = {
  drive: Folder,
  database: Database,
  erp: Building2,
  spreadsheet: Sheet,
  "open banking": Landmark,
  crm: Users,
  accounting: Calculator,
  cad: Compass,
  sdi: Receipt,
};

function iconForCategory(category: string | undefined): LucideIcon {
  if (!category) return Database;
  return CATEGORY_ICON[category.toLowerCase()] ?? Database;
}

export interface ConnectorOption {
  id: string;
  label: string;
  /** Optional vendor hint shown next to the label. */
  vendor?: string;
  /** Owner grouping label (e.g. "P.IVA 01234567890"). Ungrouped when absent. */
  company?: string;
  /** Category badge (e.g. "Database", "ERP", "Spreadsheet"). */
  category?: string;
  /** When `true`, the row is rendered as disabled with a tooltip. */
  disabled?: boolean;
  /** Tooltip on the disabled state (e.g. "Token scaduto"). */
  disabledReason?: string;
}

export interface ConnectorPickerProps {
  connectors: ConnectorOption[];
  selectedIds: string[];
  onChange: (next: string[]) => void;
  /** Show a search input when there are more than this many connectors. */
  searchThreshold?: number;
  /** Disable bulk group selection for an explicit single-connection caller. */
  allowGroupSelection?: boolean;
  className?: string;
}

const UNGROUPED_KEY = "__ungrouped__";
const UNGROUPED_LABEL = "Altri connettori";

interface Group {
  readonly key: string;
  readonly label: string;
  readonly rows: readonly ConnectorOption[];
}

/** Stable insertion-order grouping by ``company`` (ungrouped bucket last). */
function groupByCompany(connectors: readonly ConnectorOption[]): readonly Group[] {
  const order: string[] = [];
  const buckets = new Map<string, ConnectorOption[]>();
  for (const c of connectors) {
    const key = c.company ?? UNGROUPED_KEY;
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = [];
      buckets.set(key, bucket);
      order.push(key);
    }
    bucket.push(c);
  }
  // Keep the ungrouped bucket last regardless of first-seen order.
  order.sort((a, b) => {
    if (a === UNGROUPED_KEY) return 1;
    if (b === UNGROUPED_KEY) return -1;
    return 0;
  });
  return order.map((key) => ({
    key,
    label: key === UNGROUPED_KEY ? UNGROUPED_LABEL : key,
    rows: buckets.get(key) ?? [],
  }));
}

/** A checkbox that reflects an indeterminate ("some selected") state. */
const TriStateCheckbox: React.FC<{
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
  "aria-label": string;
  className?: string;
}> = ({ checked, indeterminate, onChange, className, ...rest }) => (
  <Checkbox
    checked={checked}
    indeterminate={indeterminate && !checked}
    onChange={onChange}
    className={className}
    {...rest}
  />
);

export const ConnectorPicker: React.FC<ConnectorPickerProps> = ({
  connectors,
  selectedIds,
  onChange,
  searchThreshold = 6,
  allowGroupSelection = true,
  className,
}) => {
  const [filter, setFilter] = React.useState("");
  const [collapsed, setCollapsed] = React.useState<ReadonlySet<string>>(() => new Set());

  const selectedSet = React.useMemo(() => new Set(selectedIds), [selectedIds]);

  const filtered = React.useMemo(() => {
    if (filter.trim().length === 0) return connectors;
    const q = filter.trim().toLowerCase();
    return connectors.filter(
      (c) =>
        c.label.toLowerCase().includes(q) ||
        (c.vendor ?? "").toLowerCase().includes(q) ||
        (c.company ?? "").toLowerCase().includes(q) ||
        (c.category ?? "").toLowerCase().includes(q),
    );
  }, [connectors, filter]);

  const groups = React.useMemo(() => groupByCompany(filtered), [filtered]);
  const isGrouped = groups.length > 1 || groups[0]?.key !== UNGROUPED_KEY;

  const toggle = React.useCallback(
    (id: string) => {
      if (selectedSet.has(id)) {
        onChange(selectedIds.filter((x) => x !== id));
      } else {
        onChange([...selectedIds, id]);
      }
    },
    [onChange, selectedIds, selectedSet],
  );

  /** Select / clear every enabled row in a group, leaving others untouched. */
  const toggleGroup = React.useCallback(
    (group: Group) => {
      const enabledIds = group.rows.filter((r) => !r.disabled).map((r) => r.id);
      const allSelected = enabledIds.every((id) => selectedSet.has(id));
      if (allSelected) {
        const drop = new Set(enabledIds);
        onChange(selectedIds.filter((x) => !drop.has(x)));
      } else {
        const next = new Set(selectedIds);
        for (const id of enabledIds) next.add(id);
        onChange([...next]);
      }
    },
    [onChange, selectedIds, selectedSet],
  );

  const toggleCollapse = React.useCallback((key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  if (connectors.length === 0) {
    return (
      <div
        className={cn(
          "rounded-md border border-hairline bg-cream/40 px-3 py-2 text-[11px] text-ink-3",
          className,
        )}
      >
        Nessun connettore configurato.
      </div>
    );
  }

  return (
    <div className={cn("flex w-full flex-col gap-1", className)}>
      {connectors.length > searchThreshold ? (
        <input
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Cerca connettore…"
          aria-label="Cerca connettore"
          className="mb-1 w-full rounded-md border border-hairline bg-white px-2 py-1 text-[11px] outline-none focus:border-ando-orange focus:ring-1 focus:ring-ando-orange/40"
        />
      ) : null}

      <div className="flex max-h-64 flex-col gap-1 overflow-y-auto">
        {groups.map((group) => {
          const enabled = group.rows.filter((r) => !r.disabled);
          const selectedInGroup = enabled.filter((r) => selectedSet.has(r.id)).length;
          const allSelected = enabled.length > 0 && selectedInGroup === enabled.length;
          const isCollapsed = collapsed.has(group.key);
          return (
            <div key={group.key} className="flex flex-col">
              {isGrouped ? (
                <div className="flex items-center gap-2 px-1 py-0.5">
                  {allowGroupSelection && enabled.length > 0 ? (
                    <TriStateCheckbox
                      checked={allSelected}
                      indeterminate={selectedInGroup > 0}
                      onChange={() => toggleGroup(group)}
                      aria-label={`Seleziona tutti in ${group.label}`}
                    />
                  ) : (
                    <span className="w-3.5" />
                  )}
                  <button
                    type="button"
                    onClick={() => toggleCollapse(group.key)}
                    aria-expanded={!isCollapsed}
                    className="flex flex-1 items-center gap-1 bg-transparent text-left font-semibold text-[10px] text-ink-3 uppercase tracking-wide transition-colors hover:text-ink-2 focus-visible:outline-none"
                  >
                    <ChevronDown
                      size={11}
                      className={cn("shrink-0 transition-transform", isCollapsed && "-rotate-90")}
                    />
                    <span className="truncate">{group.label}</span>
                    <span className="font-normal text-ink-3/70">
                      {selectedInGroup > 0
                        ? `${selectedInGroup}/${group.rows.length}`
                        : group.rows.length}
                    </span>
                  </button>
                </div>
              ) : null}

              {isCollapsed ? null : (
                <div className="flex flex-col gap-0.5">
                  {group.rows.map((c) => {
                    const checked = selectedSet.has(c.id);
                    const RowIcon = iconForCategory(c.category);
                    return (
                      <label
                        key={c.id}
                        // Vendor slug (e.g. ``postgresql``) lives in the tooltip
                        // for power users; the row stays clean with a single
                        // human-readable category tag.
                        title={c.disabled ? c.disabledReason : c.vendor}
                        className={cn(
                          "flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 transition-colors",
                          isGrouped && "ml-3.5",
                          "hover:bg-ink-1/[0.04]",
                          c.disabled && !checked && "cursor-not-allowed opacity-50 hover:bg-transparent",
                        )}
                      >
                        <Checkbox
                          checked={checked}
                          disabled={c.disabled && !checked}
                          onChange={() => toggle(c.id)}
                        />
                        {/* Per-category glyph — mirrors ScopeRow (Folder /
                            FileText) so the user reads the row by icon
                            first, label second. Sizes / muted tone
                            match the other tabs verbatim for visual
                            parity across the Ambito popover. */}
                        <RowIcon aria-hidden className="h-3.5 w-3.5 shrink-0 text-ink-3" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[12px] text-ink-1">{c.label}</span>
                          {c.disabled && c.disabledReason ? (
                            <span className="block text-[10px] leading-tight text-ink-3">{c.disabledReason}</span>
                          ) : null}
                        </span>
                        {c.category ? (
                          <span className="shrink-0 rounded-full bg-ink-1/[0.06] px-1.5 py-0.5 font-medium text-[10px] text-ink-2 tracking-wide">
                            {c.category}
                          </span>
                        ) : null}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {filtered.length === 0 ? (
          <span className="px-2 py-1.5 text-[11px] text-ink-3">
            Nessun risultato per "{filter}".
          </span>
        ) : null}
      </div>

      {selectedIds.length > 0 ? (
        <div className="mt-0.5 flex items-center justify-between border-hairline border-t px-1 pt-1.5">
          <span className="text-[10.5px] text-ink-3">{selectedIds.length} selezionati</span>
          <button
            type="button"
            onClick={() => onChange([])}
            className="bg-transparent font-medium text-[10.5px] text-ink-2 transition-colors hover:text-ando-orange focus-visible:outline-none"
          >
            Deseleziona tutto
          </button>
        </div>
      ) : null}
    </div>
  );
};
ConnectorPicker.displayName = "ConnectorPicker";
